import type { Router } from 'expo-router'
import type { Id } from '@ppwr/convex/_generated/dataModel'

/**
 * MTT navigation helpers.
 *
 * Single source of truth for tournament navigation verbs. All MTT call
 * sites must go through these helpers so the stack behavior stays
 * consistent and animations match user intent.
 *
 * ## Routing model
 *
 * The tournament UI is a single route — `/mtt/[tournamentId]` (the lobby) —
 * with a tabbed interface for Chat / Leaderboard / Tables / Results.
 * The previous standalone `/leaderboard`, `/tables`, `/summary` routes
 * were folded into the lobby; navigation to "those screens" is now
 * navigation to the lobby with a `tab=` query param.
 *
 * Contract:
 *  - `open*` helpers are user-driven and use `push` (or `navigate` for
 *    lobby entry to dedupe). Back returns to the prior screen.
 *  - `redirect*` helpers are state-machine driven and use `replace`. The
 *    prior screen is discarded.
 *  - `spectatorReturn*` helpers and `closeTournament` use `dismissTo` to
 *    unwind to an existing ancestor screen.
 */

type RouterLike = Pick<Router, 'push' | 'replace' | 'navigate' | 'dismissTo' | 'back'>

type TournamentId = Id<'tournaments'> | string
type GameId = Id<'games'> | string

/** Lobby tab identifiers. Keep in sync with `TournamentLobbyScreen` tabs. */
export type LobbyTab = 'chat' | 'leaderboard' | 'tables' | 'results'

// ── User-driven navigation (push) ─────────────────────────────────────

/**
 * Open the tournament lobby on a specific tab. `navigate` (not `push`) so
 * repeated taps from `MyTournaments`, countdown toasts, side nav, etc.
 * don't stack duplicate lobby instances.
 */
export function openLobby(
  router: RouterLike,
  tournamentId: TournamentId,
  options?: { tab?: LobbyTab; extraParams?: Record<string, string> }
) {
  router.navigate({
    pathname: '/mtt/[tournamentId]',
    params: {
      tournamentId: String(tournamentId),
      ...(options?.tab ? { tab: options.tab } : {}),
      ...(options?.extraParams ?? {})
    }
  } as never)
}

export function openTables(router: RouterLike, tournamentId: TournamentId) {
  openLobby(router, tournamentId, { tab: 'tables' })
}

export function openLeaderboard(
  router: RouterLike,
  tournamentId: TournamentId,
  extraParams?: Record<string, string>
) {
  openLobby(router, tournamentId, { tab: 'leaderboard', extraParams })
}

export function openSummary(router: RouterLike, tournamentId: TournamentId) {
  openLobby(router, tournamentId, { tab: 'results' })
}

export function openMyTable(router: RouterLike, gameId: GameId) {
  router.push({
    pathname: '/game/[gameId]',
    params: { gameId: String(gameId) }
  } as never)
}

// ── State-machine redirects (replace) ─────────────────────────────────

/**
 * Used by `JoinGameSheet`, `CreateMTT`, and bust-out / tournament-end
 * flows. These screens/transitions shouldn't be back-navigable to, so we
 * replace into the lobby instead of pushing.
 *
 * The `tab` option lets callers land the user on a specific tab when the
 * state machine demands it (e.g. decline-reentry → results; finish → leaderboard).
 */
export function redirectToLobby(
  router: RouterLike,
  tournamentId: TournamentId,
  options?: { tab?: LobbyTab; extraParams?: Record<string, string> }
) {
  router.replace({
    pathname: '/mtt/[tournamentId]',
    params: {
      tournamentId: String(tournamentId),
      ...(options?.tab ? { tab: options.tab } : {}),
      ...(options?.extraParams ?? {})
    }
  } as never)
}

export function redirectToSummary(router: RouterLike, tournamentId: TournamentId) {
  redirectToLobby(router, tournamentId, { tab: 'results' })
}

export function redirectToLeaderboard(
  router: RouterLike,
  tournamentId: TournamentId,
  extraParams?: Record<string, string>
) {
  redirectToLobby(router, tournamentId, { tab: 'leaderboard', extraParams })
}

/**
 * Direct table-move redirect: replace the current game route with the
 * new game route. The lobby is NOT a transport screen in this flow —
 * the game screen handles the move directly.
 */
export function redirectToNewTable(router: RouterLike, gameId: GameId) {
  router.replace({
    pathname: '/game/[gameId]',
    params: { gameId: String(gameId) }
  } as never)
}

// ── Exits / unwinds (dismissTo) ───────────────────────────────────────

/**
 * Leave the tournament flow entirely. Used by the lobby header close
 * action and post-leave navigation.
 */
export function closeTournament(router: RouterLike) {
  router.dismissTo('/(root)/(tabs)/play' as never)
}

/**
 * Spectator footer "Table List": pop the spectated game off the stack
 * back to the lobby on the Tables tab.
 */
export function spectatorReturnToTables(router: RouterLike, tournamentId: TournamentId) {
  router.dismissTo({
    pathname: '/mtt/[tournamentId]',
    params: { tournamentId: String(tournamentId), tab: 'tables' }
  } as never)
}

/**
 * Spectator footer "Return to Lobby": pop the spectated game off the
 * stack back to the lobby (default Chat tab).
 */
export function spectatorReturnToLobby(router: RouterLike, tournamentId: TournamentId) {
  router.dismissTo({
    pathname: '/mtt/[tournamentId]',
    params: { tournamentId: String(tournamentId) }
  } as never)
}
